import { useMemo, useState } from 'react';
import { Search, Settings, X } from 'lucide-react';
import type {
  FidsTrackingRouteWarehouse,
  FidsTrackingRouteWarehouseOption,
} from 'contracts/FidsTrackingRoutes';
import { Modal, ModalCloseButton } from 'components/Shared';
import { listFidsTrackingRouteWarehouses } from 'hooks/api/useFidsTrackingRoutes';
import { apiErrorHandler } from 'services';
import { normalize } from './TrackingRouteList';
import * as S from './styles';

const digits = (text: string) => text.replace(/\D/g, '');

const place = ({ city, uf }: FidsTrackingRouteWarehouse) =>
  uf ? `${city}/${uf}` : city;

// Busca por nome, cidade ou CNPJ (com ou sem pontuação).
const matches = (warehouse: FidsTrackingRouteWarehouse, search: string) => {
  const text = normalize(search.trim());
  if (!text) return true;
  const searchDigits = digits(search);
  return (
    normalize(
      `${warehouse.name} ${place(warehouse)} ${warehouse.document}`
    ).includes(text) ||
    (searchDigits.length > 0 &&
      digits(warehouse.document).includes(searchDigits))
  );
};

interface Props {
  routeId: number;
  warehouses: FidsTrackingRouteWarehouse[];
  disabled: boolean;
  onChange: (warehouses: FidsTrackingRouteWarehouse[]) => void;
}

// Armazéns que usam o rastreio. Uma unidade pode ter vários armazéns (um por CNPJ), e cada
// armazém fica em um rastreio só.
export const LinkedWarehouses: React.FC<Props> = ({
  routeId,
  warehouses,
  disabled,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<FidsTrackingRouteWarehouseOption[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Os já vinculados ao abrir a janela aparecem primeiro (sem pular enquanto se marca).
  const [linkedAtOpen, setLinkedAtOpen] = useState<Set<number>>(new Set());

  const loadOptions = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setOptions(await listFidsTrackingRouteWarehouses());
    } catch (error) {
      setLoadError(apiErrorHandler(error).errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const open = () => {
    setSearch('');
    setSelectedIds(new Set(warehouses.map(({ id }) => id)));
    setLinkedAtOpen(new Set(warehouses.map(({ id }) => id)));
    setIsOpen(true);
    void loadOptions();
  };

  const toggle = (id: number) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = () => {
    const known = new Map<number, FidsTrackingRouteWarehouse>(
      [...warehouses, ...options].map((warehouse) => [warehouse.id, warehouse])
    );
    onChange(
      [...selectedIds]
        .flatMap((id) => {
          const warehouse = known.get(id);
          return warehouse
            ? [
                {
                  id,
                  name: warehouse.name,
                  document: warehouse.document,
                  city: warehouse.city,
                  uf: warehouse.uf,
                },
              ]
            : [];
        })
        .sort((first, second) => first.name.localeCompare(second.name, 'pt-BR'))
    );
    setIsOpen(false);
  };

  const visibleOptions = useMemo(
    () =>
      options
        .filter((option) => matches(option, search))
        .sort(
          (first, second) =>
            Number(linkedAtOpen.has(second.id)) -
            Number(linkedAtOpen.has(first.id))
        ),
    [options, search, linkedAtOpen]
  );

  return (
    <section aria-label="Armazéns vinculados" className="flex flex-col gap-3">
      <h3 className="text-sm font-bold text-slate-800">
        Armazéns vinculados ({warehouses.length})
      </h3>
      {warehouses.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhum armazém vinculado.</p>
      ) : (
        <ul aria-label="Armazéns do rastreio" className="flex flex-wrap gap-2">
          {warehouses.map((warehouse) => (
            <li
              key={warehouse.id}
              className="flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 py-1 pl-3 pr-1 text-xs text-slate-700"
              title={`${warehouse.document} · ${place(warehouse)}`}
            >
              <span className="max-w-[220px] truncate">{warehouse.name}</span>
              <button
                type="button"
                aria-label={`Desvincular ${warehouse.name}`}
                disabled={disabled}
                onClick={() =>
                  onChange(warehouses.filter(({ id }) => id !== warehouse.id))
                }
                className="flex h-5 w-5 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 disabled:opacity-40"
              >
                <X size={12} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <S.OutlineButton
        type="button"
        className="self-start"
        disabled={disabled}
        onClick={open}
      >
        <Settings size={16} aria-hidden="true" />
        Gerenciar armazéns
      </S.OutlineButton>

      {isOpen ? (
        <Modal isOpen onClickOutside={() => setIsOpen(false)}>
          <S.WarehouseDialog
            role="dialog"
            aria-modal="true"
            aria-label="Gerenciar armazéns"
          >
            <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <h3 className="font-bold text-slate-800">Gerenciar armazéns</h3>
              <ModalCloseButton onClick={() => setIsOpen(false)} />
            </header>
            <div className="flex flex-col gap-3 px-5 py-4">
              <label className="relative block">
                <span className="sr-only">Buscar armazéns</span>
                <Search
                  size={15}
                  aria-hidden="true"
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <S.SearchInput
                  type="search"
                  placeholder="Buscar por nome, CNPJ ou cidade..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  $invalid={false}
                />
              </label>
              {loadError ? (
                <div
                  role="alert"
                  className="flex flex-col items-start gap-2 text-sm text-red-700"
                >
                  <p>{loadError}</p>
                  <button
                    type="button"
                    className="font-bold underline"
                    onClick={() => void loadOptions()}
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : isLoading ? (
                <p className="py-6 text-center text-sm text-slate-500">
                  Carregando armazéns...
                </p>
              ) : visibleOptions.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">
                  Nenhum armazém encontrado.
                </p>
              ) : (
                <ul
                  aria-label="Armazéns disponíveis"
                  className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto"
                >
                  {visibleOptions.map((option) => {
                    const otherRoute =
                      option.trackingRouteId !== null &&
                      option.trackingRouteId !== routeId
                        ? option.trackingRouteName
                        : null;
                    return (
                      <li key={option.id}>
                        <label
                          className={`flex items-start gap-3 rounded border border-slate-200 px-3 py-2 text-sm ${
                            otherRoute
                              ? 'cursor-not-allowed bg-slate-50 text-slate-500'
                              : 'cursor-pointer hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selectedIds.has(option.id)}
                            disabled={Boolean(otherRoute)}
                            onChange={() => toggle(option.id)}
                          />
                          <span className="min-w-0">
                            <span className="block font-semibold text-slate-800">
                              {option.name}
                            </span>
                            <span className="block text-xs text-slate-500">
                              {`${option.document} · ${place(option)}`}
                            </span>
                            {otherRoute ? (
                              <span className="block text-xs font-semibold text-slate-600">
                                Já está no rastreio "{otherRoute}"
                              </span>
                            ) : null}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <footer className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-4">
              <span className="text-xs text-slate-600">
                {selectedIds.size === 1
                  ? '1 selecionado'
                  : `${selectedIds.size} selecionados`}
              </span>
              <div className="flex gap-2">
                <S.SecondaryButton
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancelar
                </S.SecondaryButton>
                <S.PrimaryButton
                  type="button"
                  disabled={isLoading}
                  onClick={apply}
                >
                  Aplicar
                </S.PrimaryButton>
              </div>
            </footer>
          </S.WarehouseDialog>
        </Modal>
      ) : null}
    </section>
  );
};
